import { ComponentProps } from 'react';
import { SMS } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v1/models.pb';
import { formatPhoneNumberE164 } from '@frontend/phone-numbers';
import { GroupParticipant, getGroupParticipantDisplayName } from '@frontend/thread-header-context';
import { theme } from '@frontend/theme';
import { useGetThreadData } from '../hooks/use-get-thread-data';
import { SMSContextActions } from '../types';
import { SMSThreadItem } from './thread-items/sms-thread-item';

type VirtuosoContext = {
  isSelecting?: boolean;
  selectedSmsIds?: string[];
  allIsSelected?: boolean;
  onToggleSmsSelection?: (smsId: string) => void;
  hiddenContextActions?: SMSContextActions[];
  smsIdsWithNewWritebacks: string[];
  enableSmsSelectionPredicate: (sms: SMS) => boolean;
  compact?: boolean;
};

export type SMSThreadItemResolverProps = {
  threadItem: { smsMessage: Parameters<typeof SMSThreadItem>[0]['sms'] } & Record<string, unknown>;
  participantsByPhone: Map<string, GroupParticipant>;
  contact: ReturnType<typeof useGetThreadData>['contact'];
  contactDisplayName: string;
  contactDisplayFirstName: string;
  media: { mediaId: string; src?: string; contentType?: string; isError?: boolean }[];
  index: number;
  threadItemId: string;
  context: VirtuosoContext;
  handleSmsImageLoaded: () => void;
  mediaMaxWidth: ComponentProps<typeof SMSThreadItem>['mediaMaxWidth'];
  hideWritebacks?: boolean;
};

/**
 * Resolves per-message sender info for group threads and renders SMSThreadItem.
 * Extracted from an IIFE in thread-list.tsx to avoid creating a new function on every render
 * and to keep thread-list.tsx from becoming too bloated.
 */
export const SMSThreadItemResolver = ({
  threadItem,
  participantsByPhone,
  contact,
  contactDisplayName,
  contactDisplayFirstName,
  media,
  index,
  threadItemId,
  context,
  handleSmsImageLoaded,
  mediaMaxWidth,
  hideWritebacks,
}: SMSThreadItemResolverProps) => {
  // For group threads, resolve the per-message sender from the pre-computed Map (O(1) lookup).
  const isGroupThread = participantsByPhone.size > 0;
  const smsPersonPhone = formatPhoneNumberE164(threadItem.smsMessage.personPhone ?? '');
  const matchedParticipant = isGroupThread && smsPersonPhone ? participantsByPhone.get(smsPersonPhone) : undefined;

  // Resolve contactName: use matched participant for group threads, fall back to thread contact.
  // For unknown participants (no person record), omit `name` so the Avatar renders the generic
  // user icon instead of initials derived from the phone number. The phone number is still
  // surfaced via `title` for the hover tooltip — mirroring groupParticipantsToAvatarProps.
  const resolvedContactName = matchedParticipant
    ? {
        name: matchedParticipant.person ? getGroupParticipantDisplayName(matchedParticipant) : undefined,
        title: getGroupParticipantDisplayName(matchedParticipant),
        firstName: matchedParticipant.person?.firstName,
        lastName: matchedParticipant.person?.lastName,
      }
    : {
        name: contactDisplayName,
        firstName: contactDisplayFirstName,
        lastName: contact?.lastName,
      };

  const resolvedThreadPersonId = matchedParticipant?.person?.personId ?? contact?.personId;

  return (
    <SMSThreadItem
      key={threadItemId + index}
      sms={threadItem.smsMessage}
      avatarProps={{
        size: 'small',
      }}
      media={media}
      css={{
        padding: theme.spacing(0, 2),
      }}
      contactName={resolvedContactName}
      threadPersonId={resolvedThreadPersonId}
      isSelecting={context.isSelecting}
      isSelected={
        context.enableSmsSelectionPredicate(threadItem.smsMessage)
          ? context.allIsSelected || context.selectedSmsIds?.includes(threadItem.smsMessage.id)
          : false
      }
      onToggleSmsSelection={() => context.onToggleSmsSelection?.(threadItem.smsMessage.id)}
      hasNewWriteback={context.smsIdsWithNewWritebacks.includes(threadItem.smsMessage.id)}
      selectionDisabled={
        !context.enableSmsSelectionPredicate(threadItem.smsMessage) || !!threadItem.smsMessage.deletedAt
      }
      onMediaImageLoaded={handleSmsImageLoaded}
      mediaMaxWidth={mediaMaxWidth}
      hiddenContextActions={context.hiddenContextActions}
      isGroupThread={isGroupThread}
      hideWritebacks={hideWritebacks}
      compact={context.compact}
    />
  );
};
